import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/feature/JsonLd';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comicCategories, getComicsByCategory, getCategoryBySlug } from '@/mocks/comics';
import { getWebPageSchema, getBreadcrumbSchema, getCollectionPageSchema } from '@/mocks/schemas';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeCategory = getCategoryBySlug(slug || 'romance');
  const comics = getComicsByCategory(slug || 'romance');

  const categoryName = activeCategory?.name || '漫画列表';

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <JsonLd schema={getWebPageSchema(
            `好看的${categoryName}推荐 - 韩漫网`,
            activeCategory?.description || `浏览韩漫网${categoryName}分类，发现精彩韩国漫画。`,
            `https://www.manhuakr.com/category/${slug}`
          )} />
          <JsonLd schema={getBreadcrumbSchema([
            { name: '首页', item: 'https://www.manhuakr.com' },
            { name: categoryName },
          ])} />
          <JsonLd schema={getCollectionPageSchema(
            `好看的${categoryName}推荐 - 韩漫网`,
            activeCategory?.description || '',
            `https://www.manhuakr.com/category/${slug}`
          )} />

          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName },
            ]}
          />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              好看的{categoryName}推荐
            </h1>
            {activeCategory && (
              <p className="text-foreground-500 text-sm">{activeCategory.description}</p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8 overflow-x-auto pb-2">
            {comicCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-700 border border-secondary-200/70 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {comics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {comics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} variant="featured" />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-secondary-100 text-secondary-500">
                <i className="ri-bookmark-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
              <Link to="/" className="inline-block mt-4 text-primary-500 hover:text-primary-600 font-medium text-sm whitespace-nowrap">
                返回首页
              </Link>
            </div>
          )}

          <div className="mt-10 p-6 rounded-xl bg-background-100 border border-secondary-200/70">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">
              关于{categoryName}
            </h2>
            <p className="text-foreground-600 text-sm leading-relaxed">
              {activeCategory
                ? `韩漫网为您精选${activeCategory.count}部优质${activeCategory.name}作品。${activeCategory.description}，我们每日更新最新章节，让您享受流畅的在线阅读体验。`
                : `浏览韩漫网的${categoryName}分类，发现更多精彩韩国漫画。`}
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}